import { IDeviceAdapter, PersonData, DeviceOperation, DeviceInfo } from './base';
import {
  SessionInfo,
  SessionEstablishmentRequest,
  SessionEstablishmentResult,
} from '../session/types';
import { md5 } from '../../utils/hash';

/**
 * ControlID Device Adapter (Face Recognition)
 * Formats payloads for ControlID face recognition devices
 */
export class ControlIDAdapter implements IDeviceAdapter {
  vendor = 'controlid' as const;

  /**
   * Format person sync payload for ControlID
   * Endpoint: /create_objects.fcgi (NEW database-style API)
   *
   * IMPORTANT: iDFace devices use the NEW ControlID API (create_objects.fcgi)
   * Backend reference: iot-connect/adapters/controlid/controlid_face_adapter.go:282-305
   *
   * Format:
   * {
   *   "object": "users",
   *   "values": [{
   *     "registration": "person_code",  // NOT user_id!
   *     "name": "full name",
   *     "image": "base64_photo",        // NOT face_image!
   *     "expires": unix_timestamp       // NOT valid_until!
   *   }]
   * }
   */
  async formatSyncPersonPayload(personData: PersonData): Promise<any> {
    const fullName = `${personData.first_name} ${personData.last_name}`.trim();

    // Use person_id as registration (backend uses PersonCode)
    const registration = personData.person_id;

    // Download and convert photo to base64
    let imageBase64 = '';
    const photoUrl = personData.identity_photo_url || personData.profile_picture_url;

    if (photoUrl) {
      if (photoUrl.startsWith('data:image')) {
        // Already base64
        const commaIndex = photoUrl.indexOf(',');
        imageBase64 = photoUrl.substring(commaIndex + 1);
        console.log(` [ControlID] Using data URL photo for ${fullName}`);
      } else {
        // Download photo from URL and convert to base64
        try {
          console.log(` [ControlID] Downloading photo for ${fullName}: ${photoUrl}`);
          const response = await fetch(photoUrl);
          if (response.ok) {
            const blob = await response.blob();
            const base64 = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const result = reader.result as string;
                // Strip data:image/...;base64, prefix
                const base64Data = result.split(',')[1];
                resolve(base64Data);
              };
              reader.readAsDataURL(blob);
            });
            imageBase64 = base64;
            console.log(` [ControlID] Downloaded photo for ${fullName}: ${imageBase64.length} bytes`);
          } else {
            console.warn(` [ControlID] Failed to download photo for ${fullName}: HTTP ${response.status}`);
          }
        } catch (error) {
          console.error(` [ControlID] Error downloading photo for ${fullName}:`, error);
        }
      }
    }

    // Calculate expiration timestamp (Unix timestamp in seconds)
    // Use validUntil if provided (visitors), otherwise default to 10 years from now
    const expiresDate = personData.validUntil ? new Date(personData.validUntil) : (() => {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 10);
      return d;
    })();
    const expiresTimestamp = Math.floor(expiresDate.getTime() / 1000);

    // Build user object (NEW database-style API format)
    // NOTE: This device does NOT support "image" field in users table
    // Photos must be uploaded separately (TODO: find correct endpoint/table)
    const userValue: any = {
      registration: registration,  // Use person_id as registration
      name: fullName,
      expires: expiresTimestamp,  // Unix timestamp
    };

    if (imageBase64) {
      console.log(` [ControlID] Photo ready for ${fullName} (${imageBase64.length} bytes) but device doesn't support image in users table`);
      console.log(`TODO: Upload photo separately after user creation`);
    } else {
      console.log(` [ControlID] No photo for ${fullName}`);
    }

    // Return NEW database-style API format
    return {
      object: 'users',
      values: [userValue],
    };
  }

  /**
   * Format person removal payload for ControlID
   * Endpoint: /destroy_objects.fcgi (NEW database-style API)
   *
   * When devicePersonId is a pure number string (numeric device ID):
   *   → uses { object: "users", ids: [N] }  (preferred, most reliable)
   * When devicePersonId is a UUID (registration field):
   *   → uses { object: "users", where: { registration: "uuid" } }  (fallback)
   */
  formatRemovePersonPayload(devicePersonId: string): any {
    // Safety guard: never allow empty/null/undefined — would delete ALL users on device
    if (!devicePersonId || devicePersonId.trim() === '') {
      throw new Error('formatRemovePersonPayload: devicePersonId is required and cannot be empty');
    }

    // Detect numeric device ID (stored as string)
    const numericId = /^\d+$/.test(devicePersonId) ? parseInt(devicePersonId, 10) : null;

    if (numericId !== null) {
      // CRITICAL: Use nested where format — NOT "ids: [N]"
      // ControlID ignores unknown fields like "ids", treating the request
      // as "no filter" = delete ALL users. The correct format matches RFID adapter:
      // { "object": "users", "where": { "users": { "id": [N] } } }
      return {
        object: 'users',
        where: {
          users: { id: [numericId] },
        },
      };
    }

    // Fallback: delete by registration UUID using nested where format
    return {
      object: 'users',
      where: {
        users: { registration: [devicePersonId] },
      },
    };
  }

  /**
   * Format face photo upload payload
   * Uses /user_set_image.fcgi endpoint with BINARY image data
   *
   * ControlID API Reference:
   * https://www.controlid.com.br/docs/access-api-en/facial-recognition/facial-enroll/
   *
   * Endpoint: /user_set_image.fcgi?user_id=X&timestamp=Y&match=Z
   * Method: POST
   * Content-Type: application/octet-stream
   * Body: RAW BINARY IMAGE DATA
   */
  formatUploadFacePayload(employeeNo: string | number, photoBase64: string): any {
    console.log(` [ControlID] Formatting face upload for user_id: ${employeeNo}, photo size: ${photoBase64.length} bytes`);

    // Current timestamp
    const timestamp = Math.floor(Date.now() / 1000);

    // Use batch upload endpoint with JSON format (easier than binary)
    // API: /user_set_image_list.fcgi
    // Format: { "user_images": [{ "user_id": <id>, "timestamp": <ts>, "image": "<base64>" }] }
    return {
      user_images: [{
        user_id: employeeNo,   // Numeric device user ID
        timestamp: timestamp,   // Unix timestamp (REQUIRED!)
        image: photoBase64,     // Base64 encoded JPEG
      }]
    };
  }

  /**
   * Format load people payload for ControlID
   * Endpoint: /load_objects.fcgi
   */
  formatLoadPeoplePayload(payload?: any): any {
    if (payload?.name) {
      // Filter by exact name — used to check if a person already exists
      // (registration is often null for users added via backend add_user.fcgi)
      return {
        object: 'users',
        where: { users: { name: [payload.name] } },
      };
    }
    if (payload?.registration) {
      return {
        object: 'users',
        where: { users: { registration: [payload.registration] } },
      };
    }
    return { object: payload?.objectType || 'users' };
  }

  /**
   * Parse ControlID load_objects response to common format
   * Input: { "users": [{ "id": 123, "registration": "abc", "name": "João" }] }
   * Output: [{ "employeeNo": "abc", "name": "João", "userType": "normal" }]
   */
  parseLoadPeopleResponse(responseData: any): Array<{
    employeeNo: string;
    name: string;
    userType: string;
  }> {
    const usersArray = responseData?.users || (Array.isArray(responseData) ? responseData : []);

    return usersArray.map((user: any) => ({
      employeeNo: user.registration?.toString() || user.id?.toString() || '',
      name: user.name?.toString() || '',
      userType: 'normal',
    }));
  }

  /**
   * Generate device person ID from PersonData
   * ControlID uses MD5 hash of person_id (matching backend)
   *
   * Backend reference: iot-connect/internal/service/sync_service.go:280-282
   */
  generateDevicePersonId(personData: PersonData): string {
    return md5(personData.person_id);
  }

  /**
   * Get endpoint for operation
   */
  getEndpoint(operation: DeviceOperation, device: DeviceInfo): string {
    const ip = device.ip_address || device.local_ip_address;
    if (!ip) throw new Error('No IP address available for device');

    const cleanIp = ip.replace(/^https?:\/\//, '');
    let deviceIp = cleanIp;
    let port = device.port || '80';
    if (cleanIp.includes(':')) {
      const parts = cleanIp.split(':');
      deviceIp = parts[0];
      port = parts[1];
    }

    const protocol = port === '443' ? 'https' : 'http';
    const base = `${protocol}://${deviceIp}:${port}`;

    switch (operation) {
      case DeviceOperation.SYNC_PERSON:
        // Use NEW database-style API (without image field)
        return `${base}/create_objects.fcgi`;
      case DeviceOperation.REMOVE_PERSON:
        return `${base}/destroy_objects.fcgi`;
      case DeviceOperation.UPLOAD_FACE:
        // Use batch photo upload endpoint with JSON format
        return `${base}/user_set_image_list.fcgi`;
      case DeviceOperation.CHECK_HEALTH:
        return `${base}/system_information.fcgi`;
      case DeviceOperation.LOAD_PEOPLE:
        return `${base}/load_objects.fcgi`;
      case DeviceOperation.ENROLL_FINGERPRINT:
        return `${base}/enroll_finger.fcgi`;
      case DeviceOperation.FETCH_FINGERPRINTS:
        return `${base}/load_objects.fcgi`;
      case DeviceOperation.PUSH_FINGERPRINT:
        return `${base}/create_objects.fcgi`;
      case DeviceOperation.DELETE_FINGERPRINT:
        return `${base}/destroy_objects.fcgi`;
      default:
        throw new Error(`Unsupported operation: ${operation}`);
    }
  }

  /**
   * Get HTTP method for operation
   */
  getMethod(operation: DeviceOperation): 'GET' | 'POST' | 'PUT' | 'DELETE' {
    switch (operation) {
      case DeviceOperation.SYNC_PERSON:
        return 'POST';
      case DeviceOperation.REMOVE_PERSON:
        return 'POST';
      case DeviceOperation.UPLOAD_FACE:
        return 'POST';
      case DeviceOperation.CHECK_HEALTH:
        return 'GET';
      case DeviceOperation.LOAD_PEOPLE:
        return 'POST';
      default:
        return 'POST';
    }
  }

  // ==================== FINGERPRINT METHODS ====================

  /**
   * Format payload to start fingerprint enrollment on device.
   * Endpoint: /enroll_finger.fcgi (blocking ~60s while user places finger 3×)
   */
  formatEnrollFingerprintPayload(deviceUserID: number, fingerType: number): any {
    return {
      user_id: deviceUserID,
      finger_type: fingerType,
      timeout: 60,
    };
  }

  /**
   * Format payload to fetch fingerprint templates from device.
   * Endpoint: /load_objects.fcgi
   */
  // ControlID fingerprint templates are stored in the `templates` object (NOT `fingers`).
  // Field name: `template` (base64). ControlID finger_type: 0=common, 1=panic.

  formatFetchFingerprintsPayload(deviceUserID: number): any {
    return {
      object: 'templates',
      where: { templates: { user_id: [deviceUserID] } },
    };
  }

  /**
   * Format payload to push a fingerprint template to device.
   * Endpoint: /create_objects.fcgi
   */
  formatPushFingerprintPayload(deviceUserID: number, _fingerType: number, templateB64: string): any {
    return {
      object: 'templates',
      values: [{ user_id: deviceUserID, finger_type: 0, template: templateB64 }], // finger_type=0 means common/normal
    };
  }

  /**
   * Format payload to delete all fingerprint templates for a user from device.
   * CRITICAL: use nested where — NOT "ids" which deletes ALL templates.
   * Endpoint: /destroy_objects.fcgi
   */
  formatDeleteFingerprintPayload(deviceUserID: number, _fingerType: number): any {
    return {
      object: 'templates',
      where: { templates: { user_id: [deviceUserID] } }, // delete all templates for user
    };
  }

  // ==================== SESSION AUTHENTICATION METHODS ====================

  /**
   * Check if device requires session-based authentication
   * ControlID ALWAYS requires session authentication
   *
   * Backend reference: iot-connect/adapters/controlid/controlid_face_adapter.go:103-157
   */
  async requiresSessionAuth(device: DeviceInfo): Promise<boolean> {
    return true; // ControlID always needs session
  }

  /**
   * Detect firmware version (not needed for ControlID)
   * ControlID always uses session auth regardless of firmware
   */
  async detectFirmwareVersion?(device: DeviceInfo): Promise<null> {
    return null; // Not applicable for ControlID
  }

  /**
   * Establish session with ControlID device using login.fcgi
   * Backend reference: iot-connect/adapters/controlid/controlid_face_adapter.go:103-157
   *
   * @param request - Session establishment request
   * @returns SessionEstablishmentResult with session info
   */
  async establishSession(
    request: SessionEstablishmentRequest
  ): Promise<SessionEstablishmentResult> {
    const { device } = request;

    console.log(` [ControlID] Establishing session for device ${device.id}`);

    try {
      const ip = device.ip_address || device.local_ip_address;
      if (!ip) {
        throw new Error('No IP address available for device');
      }

      const cleanIp = ip.replace(/^https?:\/\//, '');
      let deviceIp = cleanIp;
      let port = device.port || '80';
      if (cleanIp.includes(':')) {
        const parts = cleanIp.split(':');
        deviceIp = parts[0];
        port = parts[1];
      }

      const protocol = port === '443' ? 'https' : 'http';
      const loginUrl = `${protocol}://${deviceIp}:${port}/login.fcgi`;

      console.log(` [ControlID] Logging in to ${loginUrl}`);

      // Make login request
      const loginPayload = {
        login: device.username,
        password: device.password,
      };

      const result = await window.electronAPI!.deviceHttpRequest({
        url: loginUrl,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: loginPayload,
        timeout: request.timeout || 10000,
      });

      if (!result.success) {
        console.error(` [ControlID] Login failed for device ${device.id}: ${result.error}`);
        return {
          success: false,
          error: `Login failed: ${result.error}`,
          fallbackToDigest: false, // ControlID doesn't support digest fallback
        };
      }

      // Parse response: { session: "abc123..." }
      let responseBody: any;
      if (typeof result.data === 'string') {
        try {
          responseBody = JSON.parse(result.data);
        } catch (error) {
          responseBody = { session: result.data }; // Try raw response as session token
        }
      } else {
        responseBody = result.data;
      }

      const sessionToken = responseBody.session;

      if (!sessionToken) {
        console.error(` [ControlID] No session token found in login response for device ${device.id}`);
        console.error(`Response:`, JSON.stringify(responseBody, null, 2));
        return {
          success: false,
          error: 'No session token found in login response',
          fallbackToDigest: false,
        };
      }

      console.log(` [ControlID] Got session token for device ${device.id}: ${sessionToken.substring(0, 10)}...`);

      // Create session info
      const session: SessionInfo = {
        token: sessionToken,
        expiresAt: new Date(Date.now() + 30 * 60 * 1000), // 30 minutes
        headers: {
          Cookie: `session=${sessionToken}`,
        },
        vendor: 'controlid',
        metadata: {
          sessionToken,
        },
      };

      console.log(
        ` [ControlID] Session established for device ${device.id}, expires at ${session.expiresAt.toISOString()}`
      );

      return { success: true, session };
    } catch (error: any) {
      console.error(` [ControlID] Error establishing session for device ${device.id}:`, error);
      return {
        success: false,
        error: error.message || 'Unknown error',
        fallbackToDigest: false,
      };
    }
  }

  /**
   * Format URL with session parameters
   * Some ControlID endpoints use session in query parameters
   */
  formatUrlWithSession(endpoint: string, session: SessionInfo): string {
    // Some endpoints may require session as query parameter
    // For now, return endpoint as-is (session is in Cookie header)
    return endpoint;
  }

  /**
   * Format headers with session information
   * Adds Cookie header with session token
   */
  formatHeadersWithSession(
    baseHeaders: Record<string, string>,
    session: SessionInfo
  ): Record<string, string> {
    return {
      ...baseHeaders,
      ...session.headers,
    };
  }

  /**
   * Check if session is still valid (not expired)
   */
  isSessionValid(session: SessionInfo): boolean {
    return new Date() < session.expiresAt;
  }
}
